import React, { useEffect, useState } from 'react';
import { FaCheck, FaRegCopy } from 'react-icons/fa';
import Modal from '../Modal';
import AsyncButton from '../AsyncButton';
import JsonView from './JsonView';
import { sessionFetch } from '../../session-client';

export interface ToolCallDetail {
  toolCallId: string | null;
  name: string;
  input: unknown;
  output: unknown;
  status: 'succeeded' | 'failed';
  /**
   * What actually happened, as opposed to `status`, which only says the tool
   * dispatched. Absent on turns recorded before this was added.
   */
  outcome?: 'ok' | 'returned_error' | 'threw';
  /** Model-provided action_summary status line; absent on older turns */
  summary?: string;
  /** Wall-clock run time recorded in the action log; absent on older turns */
  durationMs?: number;
  /**
   * Present when the output was too large to inline and was stored instead.
   * The recorded output is then the pointer the model received; the stored
   * text is downloadable for seven days.
   */
  spill?: { handle: string; bytes: number; id?: number };
}

interface SpilledPointer {
  spilled: true;
  handle: string;
  spillId?: number;
  note: string;
  bytes: number;
  lines: number;
  head: string;
  tail: string;
  truncated?: 'middle' | 'ceiling';
}

function asSpilledPointer(value: unknown): SpilledPointer | null {
  let parsed = value;
  if (typeof parsed === 'string') {
    const trimmed = parsed.trim();
    if (!trimmed.startsWith('{')) return null;
    try {
      parsed = JSON.parse(trimmed);
    } catch {
      return null;
    }
  }
  if (typeof parsed !== 'object' || parsed === null) return null;
  const record = parsed as Record<string, unknown>;
  if (record.spilled !== true || typeof record.handle !== 'string') return null;
  return record as unknown as SpilledPointer;
}

export function toolOutputUrl(
  teamId: string,
  spillId: number,
  download = false,
): string {
  const base = `/api/teams/${teamId}/tool-outputs/${spillId}`;
  return download ? `${base}?download=1` : base;
}

/** Builds the URL for a turn's recorded tool-call details (shared with ChatItem's lazy history-pill fetch) */
export function toolCallsUrl(
  teamId: string,
  conversationId: number,
  messageId: number,
): string {
  return `/api/teams/${teamId}/chat-conversations/${conversationId}/messages/${messageId}/tool-calls`;
}

interface ToolCallModalProps {
  /**
   * Fetch-path props (RELOADED turns): the persisted action log has no
   * per-call data on the client, so the modal fetches it from the turn's
   * tool-calls endpoint. Omitted (and unused) when `hasInline` is true.
   */
  teamId?: string;
  conversationId?: number;
  messageId?: number;
  toolCallId: string;
  functionName: string;
  onClose: () => void;
  /**
   * LIVE turns stream input/output straight onto the client's tool segment
   * (see useTeamChatSocket's tool_activity handling) — no fetch needed.
   * When true, `inlineInput`/`inlineOutput` are rendered directly and the
   * fetch-by-messageId effect is skipped entirely.
   */
  hasInline?: boolean;
  inlineInput?: unknown;
  inlineOutput?: unknown;
  /** A live tool that started but hasn't finished yet: no output to show */
  outputPending?: boolean;
  /** Live turns: the segment's action_summary, rendered as a subtitle
   *  (reloaded turns get it from the fetched ToolCallDetail instead) */
  summary?: string;
  /** Live turns: wall-clock run time measured on the client's tool segment
   *  (reloaded turns get it from the fetched ToolCallDetail instead) */
  durationMs?: number;
}

/** "347 ms" / "2.4 s" / "1.5 min", for the modal's response-time line */
export function formatDuration(durationMs: number): string {
  if (durationMs < 1000) return `${Math.round(durationMs)} ms`;
  if (durationMs < 60_000) return `${(durationMs / 1000).toFixed(1)} s`;
  return `${(durationMs / 60_000).toFixed(1)} min`;
}

/** Depth cap for unwrapping JSON-in-string; real payloads nest once or twice. */
const MAX_JSON_UNWRAP_DEPTH = 6;

/**
 * Resolve a recorded tool value into something renderable as structure.
 *
 * Tool responses are persisted JSON.stringify'd, and a tool that returns
 * structured data commonly hands back an envelope whose payload is ITSELF a
 * JSON string. Parsing only the outer layer left that payload as a single
 * string leaf, which JsonView prints as one enormous unwrapped line — the
 * whole response technically present and completely unreadable. So strings
 * are parsed at every depth, not just the top.
 *
 * Only a parse that yields an object or array is taken. A string like "12" or
 * "true" is left alone: turning it into a number or boolean would misreport
 * what the tool actually returned, which matters more here than tidiness.
 */
export function resolveToolValue(
  value: unknown,
  depth = 0,
): { resolved: unknown; text: string } {
  const resolved = unwrap(value, depth);
  const text =
    resolved !== null && typeof resolved === 'object'
      ? JSON.stringify(resolved, null, 2)
      : resolved === undefined || resolved === null
        ? ''
        : String(resolved);
  return { resolved, text };
}

function unwrap(value: unknown, depth: number): unknown {
  if (depth >= MAX_JSON_UNWRAP_DEPTH) return value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    const structural =
      (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
      (trimmed.startsWith('[') && trimmed.endsWith(']'));
    // The OUTER layer is always JSON.parse'd, whatever it decodes to: the
    // whole recorded value is persisted JSON.stringify'd, so a plain-string
    // response arrives quoted and has to be unquoted to read as text.
    // NESTED strings are only parsed when they look like an object or array,
    // so a payload field holding "12" stays the string the tool returned.
    if (depth > 0 && !structural) return value;
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed !== null && typeof parsed === 'object') {
        return unwrap(parsed, depth + 1);
      }
      if (depth === 0) return parsed;
    } catch {
      // Not JSON after all: keep the raw string
    }
    return value;
  }
  if (Array.isArray(value)) return value.map((item) => unwrap(item, depth + 1));
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, item]) => [
        key,
        unwrap(item, depth + 1),
      ]),
    );
  }
  return value;
}

/**
 * Copies the rendered value verbatim — what you read is what you paste.
 *
 * An AsyncButton rather than a native one: the handler awaits the clipboard
 * write, and the native-server-action audit asks async handlers to migrate
 * here rather than be allowlisted. Each pane's button keeps its own accessible
 * name, since two of them sit on screen at once.
 */
const CopyValueButton: React.FC<{ text: string; label: string }> = ({
  text,
  label,
}) => {
  const [copied, setCopied] = useState(false);
  return (
    <AsyncButton
      type="button"
      className="tool-call-copy"
      data-testid={`tool-call-copy-${label}`}
      title={`Copy ${label} to clipboard`}
      aria-label={`Copy ${label} to clipboard`}
      disabled={!text}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        } catch {
          // Clipboard unavailable (insecure origin, denied permission); the
          // value stays selectable in the pane.
        }
      }}>
      {copied ? <FaCheck size={12} /> : <FaRegCopy size={12} />}
      <span>{copied ? 'Copied' : 'Copy'}</span>
    </AsyncButton>
  );
};

function formatSpillBytes(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${Math.round(bytes / (1024 * 1024))} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} bytes`;
}

/**
 * Output that was too large to keep in the conversation.
 *
 * The output pane is the pointer itself, exactly as the agent received it —
 * the note, the preview, and the handle it read the rest back with. Showing
 * the stored text here instead would make this pane a record of something that
 * never happened, and someone debugging a turn needs to see what the model
 * actually had. The full text is a download rather than an inline view for the
 * same reason, and because it can run to tens of megabytes.
 */
const SpilledOutputSection: React.FC<{
  pointer: SpilledPointer;
  teamId?: string;
}> = ({ pointer, teamId }) => (
  <>
    <div className="tool-call-modal-section">
      <h3 className="tool-call-modal-section-heading">Output</h3>
      <CopyValueButton text={JSON.stringify(pointer, null, 2)} label="output" />
    </div>
    <p className="descriptionText" data-testid="tool-call-output-spilled">
      {formatSpillBytes(pointer.bytes)} stored as {pointer.handle} — the agent
      received the pointer below, not the output
      {pointer.truncated === 'middle' &&
        '. It was too large to keep whole, so the middle was discarded'}
      {pointer.truncated === 'ceiling' &&
        '. It exceeded the read ceiling, so its end was never read'}
    </p>
    <div className="tool-call-modal-pre" data-testid="tool-call-output">
      <JsonView value={pointer as unknown as Record<string, unknown>} />
    </div>
    {pointer.spillId !== undefined && teamId !== undefined && (
      <p className="descriptionText">
        <a
          href={toolOutputUrl(teamId, pointer.spillId, true)}
          download
          data-testid="tool-call-output-download">
          Download the full output
        </a>{' '}
        ({formatSpillBytes(pointer.bytes)}, kept for seven days)
      </p>
    )}
  </>
);

/**
 * A titled input/output pane: heading on the left, copy control on the right,
 * value below. Objects and arrays get the structured JsonView tree; anything
 * else renders as text without JSON quoting.
 */
const ToolValueSection: React.FC<{
  heading: string;
  copyLabel: string;
  value: unknown;
  testId: string;
}> = ({ heading, copyLabel, value, testId }) => {
  const { resolved, text } = resolveToolValue(value);
  return (
    <>
      <div className="tool-call-modal-section">
        <h3 className="tool-call-modal-section-heading">{heading}</h3>
        <CopyValueButton text={text} label={copyLabel} />
      </div>
      {resolved !== null && typeof resolved === 'object' ? (
        <div className="tool-call-modal-pre" data-testid={testId}>
          <JsonView value={resolved} />
        </div>
      ) : (
        <pre className="tool-call-modal-pre" data-testid={testId}>
          {text || '(none)'}
        </pre>
      )}
    </>
  );
};

/**
 * Flags a call whose tool reported failure. Without this the modal rendered an
 * errored call identically to a successful one — the output string was the only
 * clue — which is how agents claiming work that had actually failed went
 * unnoticed.
 */
const OutcomeBadge: React.FC<{ outcome?: ToolCallDetail['outcome'] }> = ({
  outcome,
}) => {
  if (outcome !== 'returned_error' && outcome !== 'threw') return null;
  return (
    <p
      className="tool-call-modal-outcome"
      role="status"
      data-testid={`tool-call-outcome-${outcome}`}>
      {outcome === 'threw'
        ? 'This tool call failed before returning.'
        : 'This tool ran but reported an error — the action did not take effect.'}
    </p>
  );
};

/** The response-time line, shown under the summary when timing is known */
const DurationLine: React.FC<{ durationMs?: number }> = ({ durationMs }) =>
  durationMs === undefined ? null : (
    <p className="tool-call-modal-duration" data-testid="tool-call-duration">
      Took {formatDuration(durationMs)}
    </p>
  );

/**
 * Shows one tool call's recorded input parameters and output, fetched from
 * the turn's tool-calls endpoint. Opened by clicking an inline tool pill in
 * ChatThinkingView.
 */
const ToolCallModal: React.FC<ToolCallModalProps> = ({
  teamId,
  conversationId,
  messageId,
  toolCallId,
  functionName,
  onClose,
  hasInline,
  inlineInput,
  inlineOutput,
  outputPending,
  summary,
  durationMs,
}) => {
  const [loading, setLoading] = useState(!hasInline);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<ToolCallDetail | null>(null);

  useEffect(() => {
    // Inline mode (live turns) already has everything it needs on the
    // client's tool segment — never fetch.
    if (hasInline) return;
    if (!teamId || conversationId === undefined || messageId === undefined) {
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setDetail(null);
    sessionFetch(toolCallsUrl(teamId, conversationId, messageId), {
      credentials: 'include',
    })
      .then((response) => {
        if (!response.ok)
          throw new Error(`Request failed (${response.status})`);
        return response.json();
      })
      .then((data: { toolCalls?: ToolCallDetail[] }) => {
        if (cancelled) return;
        const toolCalls = data.toolCalls || [];
        const match =
          toolCalls.find((call) => call.toolCallId === toolCallId) ||
          toolCalls.find((call) => call.name === functionName) ||
          null;
        setDetail(match);
      })
      .catch((requestError) => {
        console.error('Error loading tool call details:', requestError);
        if (!cancelled) setError('Could not load this tool call.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [hasInline, teamId, conversationId, messageId, toolCallId, functionName]);

  return (
    <Modal isOpen onClose={onClose} title={functionName} maxWidth="640px">
      <div data-testid="tool-call-modal">
        {hasInline ? (
          <>
            {summary && (
              <p
                className="tool-call-modal-summary"
                data-testid="tool-call-summary">
                {summary}
              </p>
            )}
            {!outputPending && <DurationLine durationMs={durationMs} />}
            <ToolValueSection
              heading="Input parameters"
              copyLabel="input"
              value={inlineInput}
              testId="tool-call-input"
            />
            {outputPending ? (
              <>
                <h3 className="tool-call-modal-section-heading">Output</h3>
                <p
                  className="descriptionText"
                  data-testid="tool-call-output-pending">
                  Output will appear when the tool finishes.
                </p>
              </>
            ) : asSpilledPointer(inlineOutput) ? (
              <SpilledOutputSection
                pointer={asSpilledPointer(inlineOutput) as SpilledPointer}
                teamId={teamId}
              />
            ) : (
              <ToolValueSection
                heading="Output"
                copyLabel="output"
                value={inlineOutput}
                testId="tool-call-output"
              />
            )}
          </>
        ) : loading ? (
          <p className="descriptionText">Loading tool call…</p>
        ) : error ? (
          <div className="projects-error">{error}</div>
        ) : detail ? (
          <>
            {detail.summary && (
              <p
                className="tool-call-modal-summary"
                data-testid="tool-call-summary">
                {detail.summary}
              </p>
            )}
            <OutcomeBadge outcome={detail.outcome} />
            <DurationLine durationMs={detail.durationMs} />
            <ToolValueSection
              heading="Input parameters"
              copyLabel="input"
              value={detail.input}
              testId="tool-call-input"
            />
            {asSpilledPointer(detail.output) ? (
              <SpilledOutputSection
                pointer={asSpilledPointer(detail.output) as SpilledPointer}
                teamId={teamId}
              />
            ) : (
              <ToolValueSection
                heading="Output"
                copyLabel="output"
                value={detail.output}
                testId="tool-call-output"
              />
            )}
          </>
        ) : (
          <p className="descriptionText">
            No recorded details for this tool call.
          </p>
        )}
      </div>
    </Modal>
  );
};

export default ToolCallModal;
